import { mdiWrench } from '@mdi/js';
import { api } from 'api';
import { Currency } from 'common/classes/currency';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import { TableTitle } from 'common/components/tables';
import { CurrencyCodes } from 'common/configs/currency';
import { AccountMovement, Xrates } from 'common/state/accounts/actions';
import {
  AdjustmentTypes,
  requestAccountMovements,
} from 'common/state/accounts/thunkActions';
import { Customer, CustomerSummary } from 'common/state/customers/actions';
import React, { useEffect, useMemo, useState } from 'react';

export interface AdjustmentsTableProps {
  customer?: Customer;
  summaries?: CustomerSummary[];
  allSummaries?: CustomerSummary[];
  xrates?: Xrates;
  isLoading?: boolean;
  error?: React.ReactNode;
}

interface TransactionSplit {
  account: string;
  amount: string;
  amount_xn: string;
  currency: CurrencyCodes;
  description?: string;
  id: number;
}

interface Transaction {
  type: AdjustmentTypes;
  cr_date: string;
  currency: CurrencyCodes;
  description: string;
  id: number;
  post_date: string;
  splits: TransactionSplit[];
}

interface AdjustmentMovement extends Omit<AccountMovement, 'transaction'> {
  sender: TransactionSplit;
  reciever?: TransactionSplit;
  transaction: Transaction;
}

export interface AdjustmentRow extends AdjustmentMovement, CustomerSummary {}

export const AdjustmentsTable = ({
  customer,
  summaries,
  allSummaries,
  xrates,
  isLoading,
  error,
}: AdjustmentsTableProps) => {
  const [rows, setRows] = useState<AdjustmentRow[]>([]);
  const [movementsLoading, setMovementsLoading] = useState(false);

  useEffect(() => {
    if (!summaries?.length) {
      setRows([]);
      return;
    }

    setMovementsLoading(true);

    Promise.all(
      summaries.map((summary) =>
        requestAccountMovements(`${summary.client_account_code}.adjustments`, {
          limit: 1000,
        }).then((response) =>
          response.data.results.map((movement) => ({ ...summary, ...movement }))
        )
      )
    )
      .then((responses) =>
        responses.flat().sort((rowA, rowB) => {
          if (rowA.post_date > rowB.post_date) return -1;
          if (rowA.post_date < rowB.post_date) return 1;
          return 0;
        })
      )
      .then((rows) => {
        Promise.all(
          rows.map((row) =>
            api
              .get<Transaction>(row.transaction)
              .then((response) => response.data)
              .then((transaction) => {
                const _row: Partial<AdjustmentRow> = {
                  ...row,
                  transaction: transaction,
                };
                _row.sender = transaction.splits.find(
                  (split) =>
                    split.account.includes(`customer.${row.customer_id}`) &&
                    !split.account.includes('adjustments')
                );
                _row.reciever = transaction.splits.find(
                  (split) =>
                    !split.account.includes(`customer.${row.customer_id}`) &&
                    !split.account.includes('adjustments')
                );
                return _row as AdjustmentRow;
              })
          )
        ).then((rows) => {
          setMovementsLoading(false);
          setRows(rows);
        });
      });
  }, [summaries]);

  const columns = useMemo(
    (): ColumnProp<AdjustmentRow>[] => [
      {
        key: 'date',
        title: 'Date',
        valueFormatter: (row) => new Date(row.post_date).toLocaleString(),
      },
      { key: 'description', title: 'Description' },
      {
        key: 'contact',
        title: 'Contact',
        valueFormatter: () =>
          customer
            ? `${customer.first_name} ${customer.last_name}`
            : 'Loading...',
      },
      {
        key: 'account',
        title: 'Account',
        valueFormatter: (row) => row.customer_name,
      },
      {
        key: 'destination',
        title: 'Destination',
        valueFormatter: (row) => {
          const customer_id = parseInt(
            row?.reciever?.account?.match(/customer.([\d]+)/)?.[1]
          );
          if (!customer_id) return '-';
          return (
            allSummaries?.find((summary) => summary.customer_id)
              ?.customer_name || '-'
          );
        },
      },
      { key: 'currency', title: 'Currency' },
      {
        key: 'amount',
        title: 'Amount',
        valueFormatter: (row) =>
          new Currency(row.amount, row.currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.amount, row.currency).toString({ symbol: false }),
        sortValueGenerator: (row) => parseFloat(row.amount),
      },
      {
        key: 'customer_currency',
        title: 'Customer Currency',
        valueFormatter: () =>
          customer ? customer.default_currency : 'Loading...',
      },
      {
        key: 'customer_amount',
        title: 'Customer Amount',
        valueFormatter: (row) =>
          customer && xrates
            ? new Currency(
                (xrates[customer.default_currency] * parseFloat(row.amount)) /
                  xrates[row.currency],
                customer.default_currency
              ).toElement()
            : 'Loading...',
        csvFormatter: (row) =>
          customer && xrates
            ? new Currency(
                (xrates[customer.default_currency] * parseFloat(row.amount)) /
                  xrates[row.currency],
                customer.default_currency
              ).toString({ symbol: false })
            : 'Loading...',
        sortValueGenerator: (row) =>
          customer && xrates
            ? (xrates[customer.default_currency] * parseFloat(row.amount)) /
              xrates[row.currency]
            : 'Loading...',
      },
      {
        key: 'rate',
        title: 'Rate',
        valueFormatter: (row) =>
          customer && xrates
            ? (
                xrates[customer.default_currency] / xrates[row.wallet_currency]
              ).toPrecision(4)
            : 'Loading...',
      },
    ],
    [customer, allSummaries, xrates]
  );

  return (
    <>
      <TableTitle icon={mdiWrench} title="Adjustments" />
      <BaseTable
        columns={columns}
        rows={rows || []}
        csvName="Adjustments"
        isLoading={isLoading || movementsLoading}
        error={error}
        paginate={false}
      />
    </>
  );
};
